import {
  type ColumnsState,
  ProTable,
  type ProTableProps,
} from '@ant-design/pro-components';
import { useSession, useTenant } from '@zhiyue/core';
import { ConfigProvider, message } from 'antd';
import React from 'react';
import {
  getTablePreference,
  saveTablePreference,
  type TablePreference,
} from '@/services/tablePreferences';
import {
  tableScroll,
  withTableColumnDisplay,
  withTableColumnWidths,
} from './tableColumns';

type PreferenceScope = { key: string; isCurrent: () => boolean };
export const TablePreferenceContext =
  React.createContext<PreferenceScope | null>(null);

export function TablePreferenceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { currentUser } = useSession();
  const { currentTenantUuid } = useTenant();
  const key =
    currentUser?.memberUuid && currentTenantUuid
      ? `${currentTenantUuid}:${currentUser.memberUuid}`
      : '';
  const currentKey = React.useRef(key);
  currentKey.current = key;
  const scope = React.useMemo(
    () => (key ? { key, isCurrent: () => currentKey.current === key } : null),
    [key],
  );
  return (
    <TablePreferenceContext.Provider value={scope}>
      {children}
    </TablePreferenceContext.Provider>
  );
}

export const getTablePopupContainer = () =>
  (document.fullscreenElement as HTMLElement | null) || document.body;

type AppTableProps<T, Params, ValueType> = ProTableProps<
  T,
  Params,
  ValueType
> & { preferenceKey: string };

function PreferenceTable<
  T extends Record<string, any>,
  Params extends Record<string, any>,
  ValueType,
>({
  preferenceKey,
  scope,
  columnsState,
  size,
  onSizeChange,
  options,
  ...props
}: AppTableProps<T, Params, ValueType> & {
  scope: PreferenceScope | null;
}) {
  const enabled = options !== false && props.toolBarRender !== false;
  const columns = React.useMemo(
    () =>
      props.editable
        ? withTableColumnWidths(props.columns, true)
        : withTableColumnDisplay(props.columns, 'pro'),
    [props.columns, props.editable],
  );
  const defaults = React.useRef(columnsState?.defaultValue || {}).current;
  const [columnMap, setColumnMap] =
    React.useState<Record<string, ColumnsState>>(defaults);
  const [density, setDensity] = React.useState(size || 'large');
  const [loading, setLoading] = React.useState(Boolean(scope && enabled));
  const value = React.useRef<TablePreference>({
    columnsState: defaults,
    density: size === 'small' || size === 'middle' ? size : 'default',
    version: 1,
  });
  const saves = React.useRef<Promise<unknown>>(Promise.resolve());
  const normalizeColumns = React.useCallback(
    (saved: Record<string, ColumnsState>) => {
      const next = { ...defaults, ...saved };
      for (const [key, column] of Object.entries(defaults)) {
        next[key] = { ...column, ...next[key] };
        if (column.disable === true) {
          next[key] = {
            ...next[key],
            disable: true,
            show: column.show,
            fixed: column.fixed,
          };
        }
      }
      return next;
    },
    [defaults],
  );

  React.useEffect(() => {
    if (!scope || !enabled) return;
    let active = true;
    setLoading(true);
    getTablePreference(preferenceKey)
      .then((saved) => {
        if (!active || !scope.isCurrent() || !saved) return;
        const nextColumns = normalizeColumns(saved.columnsState || {});
        const nextDensity =
          saved.density === 'small' || saved.density === 'middle'
            ? saved.density
            : 'large';
        setColumnMap(nextColumns);
        setDensity(nextDensity);
        value.current = {
          columnsState: nextColumns,
          density: nextDensity === 'large' ? 'default' : nextDensity,
          version: 1,
        };
      })
      .catch(() => {
        if (active && scope.isCurrent())
          message.warning('表格习惯暂未加载，当前使用默认设置');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [preferenceKey, scope, enabled, normalizeColumns]);

  const save = (patch: Partial<TablePreference>) => {
    value.current = { ...value.current, ...patch };
    if (!scope || !enabled || loading) return;
    const snapshot = value.current;
    // Serialize writes so a slower earlier request cannot overwrite a later edit.
    saves.current = saves.current
      .then(() =>
        scope.isCurrent()
          ? saveTablePreference(preferenceKey, snapshot)
          : undefined,
      )
      .catch(() => {
        if (scope.isCurrent()) message.warning('表格习惯保存失败，请稍后重试');
      });
  };

  const pageOptions = options || {};
  const setting =
    typeof pageOptions.setting === 'object' ? pageOptions.setting : {};
  return (
    <ConfigProvider getPopupContainer={getTablePopupContainer}>
      <ProTable<T, Params, ValueType>
        {...props}
        rowSelection={
          props.rowSelection
            ? { columnWidth: 48, ...props.rowSelection }
            : props.rowSelection
        }
        tableLayout={props.tableLayout ?? 'fixed'}
        scroll={tableScroll(props.scroll)}
        columns={columns}
        className={[
          props.className,
          loading ? 'app-table-preferences-loading' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        size={density}
        onSizeChange={(next) => {
          if (!next || loading) return;
          setDensity(next);
          save({ density: next === 'large' ? 'default' : next });
          onSizeChange?.(next);
        }}
        columnsState={{
          defaultValue: defaults,
          value: columnMap,
          onChange: (next) => {
            if (loading) return;
            const normalized = normalizeColumns(next);
            setColumnMap(normalized);
            save({ columnsState: normalized });
            columnsState?.onChange?.(normalized);
          },
        }}
        options={
          options === false
            ? false
            : {
                reload: pageOptions.reload || true,
                density: true,
                setting: { ...setting, checkedReset: true, listsHeight: 480 },
                fullScreen: true,
              }
        }
      />
    </ConfigProvider>
  );
}

export default function AppTable<
  T extends Record<string, any> = Record<string, any>,
  Params extends Record<string, any> = Record<string, any>,
  ValueType = 'text',
>(props: AppTableProps<T, Params, ValueType>) {
  const scope = React.useContext(TablePreferenceContext);
  return (
    <PreferenceTable
      key={`${scope?.key || ''}:${props.preferenceKey}`}
      {...props}
      scope={scope}
    />
  );
}
